import { User } from 'firebase/auth';
import {
  addDoc,
  collection,
  doc,
  Timestamp,
  updateDoc
} from 'firebase/firestore';
import {
  useEffect,
  useState
} from "react";
import {
  SubmitHandler,
  useForm
} from 'react-hook-form';
import {
  useNavigate,
} from 'react-router-dom';
import {
  useAnalytics,
  useFirestore
} from "reactfire";
import { BaseEvent, Event, JarParentTypes, JarStatus, JarTypes } from "../../FirestoreConverters";
import { logEvent } from "firebase/analytics";
import { CollectionTypes } from "../Page/CreateEvent";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import LinkButton from "../General/LinkButton";
import Modal from "../General/Modal";
import FeeInfoModal from "./FeeInfoModal";
import TipInfoModal from "./TipInfoModal";
import JOTJarInfoModal from "./JOTJarInfoModal";

type GeneralFormValues = {
  eventName: string;
  eventType: string;
  eventParentType: string;
  feeIncluded: string;
  tippingEnabled: string;
  message: string;
  justOneTreeEnabled: string;
}

type FormProps = {
  event?: Event,
  user: User,
  setEventType?: (type: string) => void,
}

export const CreateGeneralForm = ({user, event}: FormProps) => {

  const [, setDocumentTitle] = useDocumentTitle("Create Jar")
  useEffect(() => {
    if(!event) return
    setDocumentTitle(`Edit Jar "${event.name}"`)
    setFeeSelectionRequired(
      event.parentType === JarParentTypes.NON_PROFIT || event.type === JarTypes.event
    )
  }, [event, setDocumentTitle])

  const {
    register,
    handleSubmit,
    formState: {
      errors
    },
    setValue,
    resetField,
    watch
  } = useForm<GeneralFormValues>({
    mode: "onChange"
  });
  const firestore = useFirestore()
  const navigate = useNavigate()
  const analytics = useAnalytics()

  const feeIncluded = watch("feeIncluded")

  const saveEvent = async ({
    eventName,
    eventType,
    eventParentType,
    message,
    feeIncluded,
    tippingEnabled,
    justOneTreeEnabled
  }: GeneralFormValues) => {
    // We need the organiser ref
    if (!user) throw new Error("User is null")

    // Check if an event is in the past
    if (event && event.hasEnded) throw new Error("Can't update past event")

    // Create the user ref
    const userRef = doc(firestore, "users", user.uid)

    // Construct the occasion data to save
    const eventData: BaseEvent = {
      //@todo This needs to be change to use Timestamp
      date: null,
      name: eventName.trim(),
      message: message.trim(),
      parentType: eventParentType,
      type: eventType,
      isGifting: false,
      feeIncluded: feeIncluded === "true" ? true : false,
      tippingEnabled: tippingEnabled === "true" ? true : false,
      justOneTreeEnabled: justOneTreeEnabled === "true" ? true : false,
      organiser: userRef,
      createdAt: event?.createdAt || new Timestamp(Date.now()/1000, 0),
      status: JarStatus.OPEN,
    }

    // If we already have an event, then we update, if not, we save
    let eventRef
    if (event) {
      eventRef = doc(firestore, "events", event.id)
      await updateDoc(eventRef, eventData)
    } else {
      eventRef = await addDoc(collection(firestore, "events"), eventData)
    }
    return eventRef
  }

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const onSubmit: SubmitHandler<GeneralFormValues> = async data => {

    setIsLoading(true)
    try {
      const event = await saveEvent(data)
      logEvent(analytics, "event_create", {
        jarTheme: data.eventType,
        jarType: CollectionTypes.GENERAL
      })
      navigate(`/jars/${event.id}`)
    } catch (e: any) {
      setIsLoading(false)
      logEvent(analytics, "exception", {
        description: e.toString()
      })
    }
  }

  const [showFeeInfoModal, setShowFeeInfoModal] = useState<boolean>(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false)
  const [showTipInfoModal, setShowTipInfoModal] = useState<boolean>(false)
  const [showJOTJarInfoModal, setShowJOTJarInfoModal] = useState<boolean>(false)

  const [jarTypeSelectActive, setJarTypeSelectActive] = useState<boolean>(false)
  const [jarParentType, setJarParentType] = useState<JarParentTypes>(
    event !== undefined ? event.parentType as JarParentTypes : JarParentTypes.NON_PROFIT
  )
  const [jarSubType, setJarSubType] = useState<string | undefined>(
    event !== undefined ? event.type as JarTypes : undefined
  )
  const [feeSelectionRequired, setFeeSelectionRequired] = useState(false)

  const setJarTypeValue = (type: typeof JarTypes[keyof typeof JarTypes]) => {
    if (type === undefined) return
    const typeValue = Object.keys(JarTypes)[Object.values(JarTypes).indexOf(type)]
    setValue("eventType", typeValue)
    setValue("eventParentType", jarParentType)

    setJarSubType(type)
    setJarTypeSelectActive(false)

    const isHonestyBox = jarParentType === JarParentTypes.PROFIT && type === JarTypes.honesty
    const isNonProfit = jarParentType === JarParentTypes.NON_PROFIT && typeValue !== undefined
    setFeeSelectionRequired(
      // only profit honesty boxes require it
      isHonestyBox
      ||
      // all non-profits don't require a fee
      isNonProfit
    )

    if (isHonestyBox === false && isNonProfit === false) {
      setValue("feeIncluded", "true")
    }

    resetField("tippingEnabled")
  }

  const activateTypeSelection = () => {
    if (jarTypeSelectActive === true) return setJarTypeSelectActive(false)
    resetField("eventType")
    resetField("eventParentType")
    resetField("feeIncluded")
    setJarSubType(undefined)
    setFeeSelectionRequired(false)
    setJarTypeSelectActive(true)
  }

  return (
    <div className="create-occasion-form text-center md:py-4 space-y-2">
      {showConfirmationModal === true ?
        <Modal closeFunction={() => {
            setShowConfirmationModal(false)
            setValue("feeIncluded", "true")
          }}
          title="Fees Selection Confirmation"
        >
          <div className="space-y-2 pt-2 text-left">
            <p>Flossi Pay is an alternative payment service which applies service / convenience fees on top of each payment.</p>

            <p>For the contributor to be able to pay the fees, you are required to provide them with alternative methods of feeless payment, such as cash.</p>

            <p>Flossi Pay cannot be held liable should you not follow this guidance.</p>

            <p>Please confirm that alternative methods of payment are available to your contributors.</p>
          </div>
          <div className="pt-2">
            <LinkButton
              type="button"
              clickHandler={() => {
                setShowConfirmationModal(false)
              }}
            >
              I confirm
            </LinkButton>
          </div>
        </Modal>
      : null}

      {showFeeInfoModal === true ?
        <FeeInfoModal setShowFeeInfoModal={setShowFeeInfoModal} />
      : null}

      {showTipInfoModal === true ?
        <TipInfoModal setShowTipInfoModal={setShowTipInfoModal} />
      : null}

      {showJOTJarInfoModal === true ?
        <JOTJarInfoModal setShowJOTJarInfoModal={setShowJOTJarInfoModal} />
      : null}

      {event === undefined ?
        <p className="">Fill out the information below to create your jar!</p>
      :
        <p className="p-2">Use the form below to update your jar.</p>
      }

      <form onSubmit={handleSubmit(onSubmit)} >
        <fieldset disabled={isLoading === true || (event && event.hasEnded)}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="col-span-2 sm:col-span-4">
              <label className="block text-left uppercase font-bold py-2">
                Jar Name:
              </label>
              <input
                type="text"
                placeholder="Give your jar a name (The Cake Shop, Anna's Hen Do)"
                {...register("eventName", {
                  required: "Please enter a name for your jar",
                  maxLength: {
                    value: 75,
                    message: "Please choose a shorter name"
                  }
                })}
                className="border p-2 w-full border-my-grey h-10 align-middle text-center placeholder:text-my-grey outline-my-orange capitalize rounded"
                defaultValue={event?.name}
              />
              {errors.eventName && <p className="text-red-600">{errors.eventName.message}</p>}
            </div>
            <div className="col-span-2 sm:col-span-4">
              <label className="block text-left uppercase font-bold py-2">
                Jar Type:
              </label>
              <input
                {
                  ...register(
                    "eventType",
                    {
                      required: "Please tell us how you're going to use the jar",
                      minLength: 1
                    },
                  )
                }
                defaultValue={event?.type}
                className="hidden"
                type="text"
              />
              <input
                {
                  ...register(
                    "eventParentType",
                    {
                      required: "Please tell us how you're going to use the jar",
                      minLength: 1
                    },
                  )
                }
                defaultValue={event?.parentType}
                type="text"
                className="hidden"
              />
              <input
                type="button"
                className={`border p-2 w-full border-my-grey bg-white h-10 align-middle text-center placeholder:text-my-grey outline-my-orange capitalize cursor-pointer rounded ${jarTypeSelectActive === true ? "rounded-b-none" : ""}`}
                onClick={activateTypeSelection}

                value={jarParentType !== undefined && jarSubType !== undefined?
                    `${jarParentType} / ${jarSubType}`
                  :
                    "Select a Jar Type"
                }
              />
              <div className={`border border-my-grey rounded-b border-t-0 ${jarTypeSelectActive !== true ? "hidden" : ""}`}>
                <ul className="flex p-1 cursor-pointer space-x-1">
                  <li
                    className={`flex-1 rounded p-1 ${jarParentType === "non-profit" ? " bg-my-olive text-white" : "bg-gray-200 hover:bg-my-orange hover:text-white"}`}
                    onClick={() => setJarParentType(JarParentTypes.NON_PROFIT)}
                  >
                    Non-Profit
                  </li>
                  <li
                    className={`flex-1 rounded p-1 ${jarParentType === "profit" ? " bg-my-olive text-white" : "bg-gray-200 hover:bg-my-orange hover:text-white"}`}
                    onClick={() => setJarParentType(JarParentTypes.PROFIT)}
                  >
                    Profit
                  </li>
                </ul>
                <ul className={`p-1 pt-0 ${jarParentType !== "non-profit" ? "hidden" : ""}`}>
                  <li className="underline p-1">
                      Select a non-profit use
                  </li>
                  <li
                    className="hover:bg-gray-200 rounded cursor-pointer p-1"
                    onClick={() => setJarTypeValue(JarTypes.group)}
                  >
                    Collection Between Friends
                  </li>
                  <li
                    className="hover:bg-gray-200 rounded cursor-pointer p-1"
                    onClick={() => setJarTypeValue(JarTypes.club)}
                  >
                    Amateur Sports Club
                  </li>
                  <li
                    className="hover:bg-gray-200 rounded cursor-pointer p-1"
                    onClick={() => setJarTypeValue(JarTypes.charity)}
                  >
                    Charity
                  </li>
                  <li
                    className="hover:bg-gray-200 rounded cursor-pointer p-1"
                    onClick={() => setJarTypeValue(JarTypes.event)}
                  >
                    Organising an Event
                  </li>
                  <li
                    className="hover:bg-gray-200 rounded cursor-pointer p-1"
                    onClick={() => setJarTypeValue(JarTypes.custom)}
                  >
                    Other
                  </li>
                </ul>
                <ul className={`p-1 pt-0 ${jarParentType !== "profit" ? "hidden" : ""}`}>
                  <li className="underline p-1">
                      Select a profit use
                  </li>
                  <li
                    className="hover:bg-gray-200 rounded cursor-pointer p-1"
                    onClick={() => setJarTypeValue(JarTypes.business)}
                  >
                    Business
                  </li>
                  <li
                    className="hover:bg-gray-200 rounded cursor-pointer p-1"
                    onClick={() => setJarTypeValue(JarTypes.tips)}
                  >
                    Tips at Work
                  </li>
                  <li
                    className="hover:bg-gray-200 rounded cursor-pointer p-1"
                    onClick={() => setJarTypeValue(JarTypes.honesty)}
                  >
                    Honesty Box
                  </li>
                  <li
                    className="hover:bg-gray-200 rounded cursor-pointer p-1"
                    onClick={() => setJarTypeValue(JarTypes.event)}
                  >
                    Organising an Event
                  </li>
                </ul>
              </div>
              {errors.eventType !== undefined ?
                <p className="text-red-600">{errors.eventType.message}</p>
              : null}
            </div>

            {/* Fee inclusion */}
            <label className="block text-left uppercase font-bold col-span-2 sm:col-span-4">
              Who pays the fees?
              <button type="button" onClick={() => setShowFeeInfoModal(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                </svg>
              </button>
            </label>
            <fieldset className="grid grid-cols-1 sm:grid-cols-2 col-span-2 sm:col-span-4 gap-2" disabled={jarSubType === undefined ? true : false}>
              <div className="col-span-1">
                <input type="radio"
                  id="included-true" value="true" className="hidden peer"
                  {...register(
                    "feeIncluded",
                    {
                      required: feeSelectionRequired === true ? "Please select a fee option" : false,
                    }
                  )}
                  defaultChecked={event && event.feeIncluded ? true : false}
                />
                <label htmlFor="included-true"
                  className="inline-flex items-center justify-between h-full p-3 w-full border border-my-grey cursor-pointer peer-checked:text-white peer-checked:bg-my-olive hover:text-white hover:bg-my-orange peer-checked:border-my-olive peer-checked:scale-[102%] md:peer-checked:scale-100 rounded peer-disabled:text-gray-400 peer-disabled:hover:text-gray-400 peer-disabled:bg-gray-100 peer-disabled:border-gray-100 peer-disabled:cursor-not-allowed peer-disabled:scale-100"
                >
                  <div className="w-full text-sm">
                    I will pay the small fees for each payment
                  </div>
                </label>
              </div>
              <div className="col-span-1">
                <input type="radio"
                  id="included-false" value="false" className="hidden peer"
                  {...register(
                    "feeIncluded",
                    {
                      required: feeSelectionRequired === true ? "Please select a fee option" : false,
                      onChange: (event)  => {
                        if (event.target.value === "false") {
                          setValue("tippingEnabled", "false")
                        } else {
                          resetField("tippingEnabled")
                        }
                        if (
                          event === undefined
                          || event.target === undefined
                          || event.target.value === undefined
                          || event.target.value === "true"
                          || jarSubType !== JarTypes.honesty
                        ) {
                          return
                        }
                        setShowConfirmationModal(true)
                      }
                    }
                  )}
                  defaultChecked={event && !event.feeIncluded ? true : false}
                  disabled={feeSelectionRequired === false ? true : false}
                />
                <label htmlFor="included-false"
                  className="inline-flex items-center justify-between h-full p-3 w-full border border-my-grey cursor-pointer peer-checked:text-white peer-checked:bg-my-olive hover:text-white hover:bg-my-orange peer-checked:border-my-olive peer-checked:scale-[102%] md:peer-checked:scale-100 rounded peer-disabled:text-gray-400 peer-disabled:hover:text-gray-400 peer-disabled:bg-gray-100 peer-disabled:border-gray-100 peer-disabled:cursor-not-allowed peer-disabled:scale-100"
                >
                  <div className="w-full text-sm">
                    The contributor will pay the small fees for their payment
                  </div>
                </label>
              </div>
              {errors.feeIncluded !== undefined ?
                <p className="text-red-600 col-span-1 sm:col-span-2">{errors.feeIncluded.message}</p>
              : null }
            </fieldset>

            {/* tipping selection */}
            <label className="block text-left uppercase font-bold col-span-2 sm:col-span-4">
              Allow tipping?
              <button type="button" onClick={() => setShowTipInfoModal(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                </svg>
              </button>
            </label>
            <fieldset className="grid grid-cols-1 sm:grid-cols-2 col-span-2 sm:col-span-4 gap-2" disabled={feeIncluded !== "true" ? true : false}>
              <div className="col-span-1">
                <input type="radio"
                  id="tipping-true" value="true" className="hidden peer"
                  {...register(
                    "tippingEnabled",
                    {
                      required: feeIncluded === "true" ? "Please select a tipping option" : false,
                    }
                  )}
                  defaultChecked={event && event.tippingEnabled ? true : false}
                />
                <label htmlFor="tipping-true"
                  className="inline-flex items-center justify-between h-full p-3 w-full border border-my-grey cursor-pointer peer-checked:text-white peer-checked:bg-my-olive hover:text-white hover:bg-my-orange peer-checked:border-my-olive peer-checked:scale-[102%] md:peer-checked:scale-100 rounded peer-disabled:text-gray-400 peer-disabled:hover:text-gray-400 peer-disabled:bg-gray-100 peer-disabled:border-gray-100 peer-disabled:cursor-not-allowed peer-disabled:scale-100"
                >
                  <div className="w-full text-sm">
                    Yes, allow the contributor to optionally cover fees by leaving a tip
                  </div>
                </label>
              </div>
              <div className="col-span-1">
                <input type="radio"
                  id="tipping-false" value="false" className="hidden peer"
                  {...register(
                    "tippingEnabled",
                    {
                      required: feeIncluded === "true" ? "Please select a tipping option" : false,
                    }
                  )}
                  defaultChecked={event && !event.tippingEnabled ? true : false}
                />
                <label htmlFor="tipping-false"
                  className="inline-flex items-center justify-between h-full p-3 w-full border border-my-grey cursor-pointer peer-checked:text-white peer-checked:bg-my-olive hover:text-white hover:bg-my-orange peer-checked:border-my-olive peer-checked:scale-[102%] md:peer-checked:scale-100 rounded peer-disabled:text-gray-400 peer-disabled:hover:text-gray-400 peer-disabled:bg-gray-100 peer-disabled:border-gray-100 peer-disabled:cursor-not-allowed peer-disabled:scale-100"
                >
                  <div className="w-full text-sm mt-auto">
                    No, don't give the contributor an option to leave a tip
                  </div>
                </label>
              </div>
              {errors.tippingEnabled !== undefined ?
                <p className="text-red-600 col-span-1 sm:col-span-2">{errors.tippingEnabled.message}</p>
              : null}
            </fieldset>

            {/* JOT selection */}
            <label className="block text-left uppercase font-bold col-span-2 sm:col-span-4">
              Allow 'JUST ONE Tree' Donations?
              <button type="button" onClick={() => setShowJOTJarInfoModal(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                </svg>
              </button>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 col-span-2 sm:col-span-4 gap-2">
              <div className="col-span-1">
                <input type="radio"
                  id="jot-true" value="true" className="hidden peer"
                  {...register(
                    "justOneTreeEnabled",
                    {
                      required: "Please select a donation option",
                    }
                  )}
                  defaultChecked={event && event.justOneTreeEnabled ? true : false}
                />
                <label htmlFor="jot-true"
                  className="inline-flex items-center justify-between h-full p-3 w-full border border-my-grey cursor-pointer peer-checked:text-white peer-checked:bg-my-olive hover:text-white hover:bg-my-orange peer-checked:border-my-olive peer-checked:scale-[102%] md:peer-checked:scale-100 rounded peer-disabled:text-gray-400 peer-disabled:hover:text-gray-400 peer-disabled:bg-gray-100 peer-disabled:border-gray-100 peer-disabled:cursor-not-allowed peer-disabled:scale-100"
                >
                  <div className="w-full text-sm">
                    Yes, allow the contributor to donate trees to JUST ONE Tree
                  </div>
                </label>
              </div>
              <div className="col-span-1">
                <input type="radio"
                  id="jot-false" value="false" className="hidden peer"
                  {...register(
                    "justOneTreeEnabled",
                    {
                      required: "Please select a donation option",
                    }
                  )}
                  defaultChecked={event && !event.justOneTreeEnabled ? true : false}
                />
                <label htmlFor="jot-false"
                  className="inline-flex items-center justify-between h-full p-3 w-full border border-my-grey cursor-pointer peer-checked:text-white peer-checked:bg-my-olive hover:text-white hover:bg-my-orange peer-checked:border-my-olive peer-checked:scale-[102%] md:peer-checked:scale-100 rounded peer-disabled:text-gray-400 peer-disabled:hover:text-gray-400 peer-disabled:bg-gray-100 peer-disabled:border-gray-100 peer-disabled:cursor-not-allowed peer-disabled:scale-100"
                >
                  <div className="w-full text-sm mt-auto">
                    No, don't give the contributor an option to donate to JUST ONE Tree
                  </div>
                </label>
              </div>
              {errors.justOneTreeEnabled !== undefined ?
                <p className="text-red-600 col-span-1 sm:col-span-2">{errors.justOneTreeEnabled.message}</p>
              : null}
            </div>

            {/* Description */}
            <div className="col-span-2 sm:col-span-4 mb-2">
              <label className="block text-left uppercase font-bold py-2">
                Describe your jar:
              </label>
              <textarea
                placeholder="Tell your contributors what you're collecting for..."
                {...register("message", {required: "Please tell us a bit more", maxLength: {
                  value: 500,
                  message: "The maximum length is 500 characters"
                }})}
                className="border border-my-grey p-2 w-full h-32 placeholder:text-my-grey outline-my-orange rounded"
                defaultValue={event?.message}
              />
              {errors.message && <p className="text-red-600">{errors.message.message}</p>}
            </div>
          </div>
          <LinkButton
            type="submit"
            backgroundColour="my-olive"
            disabled={isLoading}
          >
            {isLoading === true ?
              <>
                <div className="relative">{!event ? "Creating" : "Updating"}
                <svg className="animate-spin h-6 w-6 absolute left-8 inline" viewBox="0 0 24 24">
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                </div>
              </>
            : <>
            {!event ? "Create Jar!" : "Update Jar"}
            </>}
          </LinkButton>
          {/* </button> */}
        </fieldset>
      </form>
    </div>
  );
}